import { GameButton, Modal, PixelIcon } from '@/components/ui';
import { useEffects, useShop, useSound } from '@/contexts';
import { useHapticFeedback } from '@/hooks/useHapticFeedback';
import { useIsMobile } from '@/hooks/useMediaQuery';
import { getMenuHapticPattern } from '@/utils/haptics';
import { useEffect, useRef, useState } from 'react';

interface DailyRewardModalProps {
  isOpen: boolean;
  onClose: () => void;
}

interface DailyReward {
  day: number;
  coins: number;
  gems: number;
  lives?: number;
  powerups?: Record<string, number>;
}

// Define 7-day reward cycle with escalating rewards
const DAILY_REWARDS: DailyReward[] = [
  { day: 1, coins: 10, gems: 0 },
  { day: 2, coins: 50, gems: 0 },
  { day: 3, coins: 100, gems: 1 },
  { day: 4, coins: 150, gems: 2 },
  { day: 5, coins: 200, gems: 3, lives: 1 },
  { day: 6, coins: 300, gems: 5 },
  { day: 7, coins: 500, gems: 10, lives: 2, powerups: { shield: 1 } },
];

const DailyRewardModal: React.FC<DailyRewardModalProps> = ({ isOpen, onClose }) => {
  const { shopManager } = useShop();
  const { playPowerup, playCollectCoin } = useSound();
  const { triggerEffect } = useEffects();
  const { pattern } = useHapticFeedback();
  const isMobile = useIsMobile();

  const [canClaim, setCanClaim] = useState(false);
  const [currentDay, setCurrentDay] = useState(1);
  const [streak, setStreak] = useState(0);
  const [isClaiming, setIsClaiming] = useState(false);
  const [claimedToday, setClaimedToday] = useState(false);
  const [hasTriggeredOpenFeedback, setHasTriggeredOpenFeedback] = useState(false);
  const gridRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (isOpen && shopManager) {
      const status = shopManager.getDailyRewardStatus();
      setCanClaim(status.canClaim);
      setCurrentDay(status.currentDay);
      setStreak(status.streak);
      setClaimedToday(!status.canClaim && status.streak > 0);
    }
  }, [isOpen, shopManager]);

  useEffect(() => {
    if (!isOpen) {
      setHasTriggeredOpenFeedback(false);
      return;
    }
    if (hasTriggeredOpenFeedback) return;

    pattern(getMenuHapticPattern('menuOpen'));
    setHasTriggeredOpenFeedback(true);
  }, [hasTriggeredOpenFeedback, isOpen, pattern]);

  // Scroll to current day on mobile
  useEffect(() => {
    if (isOpen && isMobile && gridRef.current) {
      const currentDayElement = gridRef.current.querySelector(
        `.daily-reward-item:nth-child(${currentDay})`
      );
      if (currentDayElement) {
        setTimeout(() => {
          currentDayElement.scrollIntoView({
            behavior: 'smooth',
            inline: 'center',
            block: 'nearest',
          });
        }, 100);
      }
    }
  }, [isOpen, currentDay, isMobile]);

  const handleClaimReward = async () => {
    if (!canClaim || isClaiming) return;

    setIsClaiming(true);
    playPowerup();

    // Get today's reward
    const todayReward = DAILY_REWARDS[currentDay - 1];

    // Trigger collection effects
    if (todayReward.coins > 0) {
      playCollectCoin();
      triggerEffect({
        type: 'coinCollect',
        position: [50, 50],
        intensity: todayReward.coins / 50,
        duration: 1500,
      });
    }

    if (todayReward.gems > 0) {
      triggerEffect({
        type: 'sparkle',
        position: [50, 50],
        intensity: todayReward.gems,
        duration: 1500,
        color: '#9b59b6',
      });
    }

    // Claim the reward
    const claimed = shopManager.claimDailyReward();

    if (claimed) {
      setClaimedToday(true);
      setCanClaim(false);
      pattern(getMenuHapticPattern('rewardClaim'));

      // Celebration effect
      triggerEffect({
        type: 'confetti',
        position: [50, 50],
        intensity: 3,
        duration: 2000,
      });

      // Update streak
      const newStatus = shopManager.getDailyRewardStatus();
      setStreak(newStatus.streak);
    }

    setIsClaiming(false);
  };

  const getRewardIcon = (reward: DailyReward) => {
    if (reward.lives) return <PixelIcon name="life" size={36} color="#e74c3c" />;
    if (reward.gems > 2) return <PixelIcon name="gem" size={36} color="#9b59b6" />;
    if (reward.gems > 0) return <PixelIcon name="gem" size={36} color="#9b59b6" />;
    return <PixelIcon name="coin" size={36} color="#f39c12" />;
  };

  const getTimeUntilNextReward = () => {
    const now = new Date();
    const tomorrow = new Date(now);
    tomorrow.setDate(tomorrow.getDate() + 1);
    tomorrow.setHours(0, 0, 0, 0);

    const diff = tomorrow.getTime() - now.getTime();
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));

    return `${hours}h ${minutes}m`;
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Daily Rewards"
      subtitle={`${streak} Day Streak! • Login daily for bigger rewards`}
      variant="success"
      size="large"
      className="daily-reward-modal"
    >
      <div className="daily-rewards-container menu-delight-panel menu-delight-panel--success">
        <div className="menu-delight-panel__splashes" aria-hidden="true">
          <span className="menu-delight-panel__splash menu-delight-panel__splash--1" />
          <span className="menu-delight-panel__splash menu-delight-panel__splash--3" />
          <span className="menu-delight-panel__splash menu-delight-panel__splash--4" />
        </div>

        {/* Reward Grid */}
        <div
          className={`daily-rewards-grid ${isMobile ? 'daily-rewards-grid--mobile' : ''}`}
          ref={gridRef}
        >
          {DAILY_REWARDS.map(reward => {
            const isPast = reward.day < currentDay;
            const isCurrent = reward.day === currentDay;
            const isFuture = reward.day > currentDay;
            const isClaimedDay = isPast || (isCurrent && claimedToday);

            return (
              <div
                key={reward.day}
                className={`daily-reward-item ${isCurrent ? 'current' : ''} ${
                  isClaimedDay ? 'claimed' : ''
                } ${isFuture ? 'locked' : ''}`}
              >
                <div className="reward-day">Day {reward.day}</div>
                <div className="reward-icon">{getRewardIcon(reward)}</div>
                <div className="reward-content">
                  <div className="reward-amount">
                    {reward.coins > 0 && (
                      <span>
                        <PixelIcon name="coin" size={18} color="#f39c12" /> {reward.coins}
                      </span>
                    )}
                    {reward.gems > 0 && (
                      <span>
                        <PixelIcon name="gem" size={18} color="#9b59b6" /> {reward.gems}
                      </span>
                    )}
                    {reward.lives && (
                      <span>
                        <PixelIcon name="life" size={18} color="#e74c3c" /> +{reward.lives}
                      </span>
                    )}
                    {reward.powerups && (
                      <span>
                        <PixelIcon name="shield" size={18} color="#3498db" /> +
                        {Object.values(reward.powerups)[0]}
                      </span>
                    )}
                  </div>
                </div>
                {isClaimedDay && <div className="reward-status">OK</div>}
                {isCurrent && !claimedToday && <div className="reward-status pulse">!</div>}
              </div>
            );
          })}
        </div>

        {/* Streak Info */}
        <div className="streak-info">
          <p className="streak-message">
            {streak === 0 && 'Start your daily login streak!'}
            {streak === 1 && 'Great start! Come back tomorrow to continue your streak.'}
            {streak >= 2 && streak < 7 && `Keep it up! ${7 - streak} days until the mega reward!`}
            {streak >= 7 && "Amazing! You've completed a full week. Keep going!"}
          </p>
          {streak > 0 && (
            <div className="streak-progress">
              <div className="streak-bar">
                <div className="streak-fill" style={{ width: `${(streak % 7 || 7) * 14.28}%` }} />
              </div>
              <span className="streak-text">{streak % 7 || 7} / 7</span>
            </div>
          )}
        </div>

        {/* Collect/Close Button */}
        {/* <div className="daily-reward-footer">
          <GameButton
            onClick={onClose}
            variant="secondary"
            icon="check"
            text="Collect Rewards"
            className="collect-btn"
          />
        </div> */}

        {/* Claim Button or Timer */}
        <div className="daily-reward-actions daily-reward-footer flex justify-center">
          {canClaim ? (
            <GameButton
              onClick={handleClaimReward}
              variant="success"
              icon="gift"
              text={isClaiming ? 'Claiming...' : 'Claim Reward!'}
              disabled={isClaiming}
              className="claim-btn pulse"
            />
          ) : (
            <div className="next-reward-timer">
              <p>Next reward in:</p>
              <p className="timer-value">{getTimeUntilNextReward()}</p>
              <GameButton
                onClick={onClose}
                variant="secondary"
                icon="check"
                text="Okay"
                className="collect-btn !block !mt-2 [&>.btn-icon]:!hidden"
              />
            </div>
          )}
        </div>
      </div>
    </Modal>
  );
};

export default DailyRewardModal;
